import { getUIKitColorHSLA } from "@/helpers/style.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useTimeOffHook from "@/hooks/use-time-off.hook.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiApprovalRequestStatus } from "@/types/approvals/approval.types.ts";
import {
  ApiCalendarHolidayDate,
  ApiCalendarHolidayDateType,
} from "@/types/calendar-holiday.types.ts";
import { ApiTimeOffCalendar, ApiTimeOffLogFullPreview } from "@/types/time-off/time-off.types.ts";
import { DayCellContentArg } from "@fullcalendar/core";
import enLocale from "@fullcalendar/core/locales/en-gb";
import ruLocale from "@fullcalendar/core/locales/ru";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import {
  EmojiDisplay,
  Skeleton,
  Stack,
  Text,
  Title,
  toCssColorValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { isToday } from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import {
  buildEmployeeTimeOffDayInfoMap,
  EmployeeTimeOffDayInfo,
  EmployeeTimeOffDayInfoMap,
  getEmployeeTimeOffDayKey,
} from "./employee-time-off-calendar.helper.ts";

export interface EmployeeTimeOffCalendarProps {
  data: ApiTimeOffCalendar | undefined;
  isLoading: boolean;
  year: number;
  renderDayContent?: (
    date: Date,
    holidays: ApiCalendarHolidayDate[],
    requests: ApiTimeOffLogFullPreview[]
  ) => React.ReactNode;
}

interface CalendarMonthProps {
  monthIndex: number;
  year: number;
  dayInfoMap: EmployeeTimeOffDayInfoMap;
  locale: typeof enLocale;
  renderDayContent?: EmployeeTimeOffCalendarProps["renderDayContent"];
}

const CalendarMonth = React.memo(function CalendarMonth({
  monthIndex,
  year,
  dayInfoMap,
  locale,
  renderDayContent,
}: CalendarMonthProps) {
  const { t } = useTranslation();
  const { renderDate } = useFormatter();
  const { openViewRequest } = useTimeOffHook();

  const getDayInfo = (date: Date): EmployeeTimeOffDayInfo => {
    const key = getEmployeeTimeOffDayKey(date);
    return dayInfoMap.get(key) || { holidays: [], requests: [] };
  };

  const dayCellClassNames = (args: DayCellContentArg) => {
    const { holidays } = getDayInfo(args.date);
    if (holidays.length > 0) {
      const isNonWorking = holidays.some((h) => h.type === ApiCalendarHolidayDateType.non_working);
      if (isNonWorking) return "bg-danger/10";
    }
    return "";
  };

  const renderTooltipContent = (
    holidays: ApiCalendarHolidayDate[],
    requests: ApiTimeOffLogFullPreview[]
  ) => (
    <Stack gap="xs" className="max-w-64">
      {holidays.map((holiday, idx) => (
        <Stack key={`holiday-${idx}`} direction="horizontal" gap="xs" items="center">
          {holiday.type !== ApiCalendarHolidayDateType.working ? (
            <EmojiDisplay id=":tada:" size="xs" />
          ) : (
            ""
          )}
          <Text className="text-xs">{holiday.title}</Text>
        </Stack>
      ))}
      {requests.map((request, idx) => (
        <Stack
          key={`request-${idx}`}
          direction="horizontal"
          gap="xs"
          items="center"
          className="cursor-pointer hover:opacity-80"
          onClick={(e) => {
            e.stopPropagation();
            if (request.acl?.can_view) {
              openViewRequest(request.id);
            }
          }}
        >
          {request.time_off_data.category.emoji && (
            <EmojiDisplay id={request.time_off_data.category.emoji} size="xs" />
          )}

          <Text className="text-xs">
            {request.status === ApiApprovalRequestStatus.awaiting && `${t("common:request")}:`}
            &nbsp;
            {request.time_off_data.category.title}
          </Text>
        </Stack>
      ))}
    </Stack>
  );

  const dayCellContent = (args: DayCellContentArg) => {
    const { holidays, requests } = getDayInfo(args.date);
    const dayOfWeek = args.date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const hasRequests =
      requests.length > 0 &&
      requests.some((r) =>
        [ApiApprovalRequestStatus.approved, ApiApprovalRequestStatus.awaiting].includes(r.status)
      );
    const hasData = holidays.length > 0 || (requests.length > 0 && hasRequests);

    if (renderDayContent) {
      return renderDayContent(args.date, holidays, requests);
    }

    const dayNumber = args.dayNumberText;
    const isTodayDate = isToday(args.date);
    const isWorkDay = holidays.some((h) => h.type === ApiCalendarHolidayDateType.working);

    if (!hasData) {
      return (
        <Stack
          items={"center"}
          justify={"center"}
          className={twMerge(
            "min-h-8 rounded-xs border-2",
            isTodayDate ? "border-dark" : "border-dark/0",
            isWeekend && !isWorkDay && "bg-danger/10 text-secondary"
          )}
        >
          {dayNumber}
        </Stack>
      );
    }

    const hasHoliday = holidays.length > 0;
    const hasRequest = requests.length > 0;
    const request = requests[0];
    const requestIsApproved = request?.status === ApiApprovalRequestStatus.approved;
    const categoryColor = request?.time_off_data?.category?.color;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Stack
            className={twMerge(
              "w-full h-full items-center justify-center relative cursor-pointer min-h-8 rounded-xs border-2",
              isTodayDate ? "border-dark" : "border-dark/0",
              (hasHoliday || isWeekend) && !isWorkDay && "bg-danger/10 text-secondary"
            )}
            onClick={() => {
              if (request?.acl?.can_view) {
                openViewRequest(request.id);
              }
            }}
          >
            <span
              className={twMerge(
                "relative z-10",
                hasRequest && requestIsApproved ? "text-light" : ""
              )}
            >
              {dayNumber}
            </span>
            {hasRequest && (
              <div
                className={twMerge(
                  "absolute inset-0.5 rounded-xs",
                  !requestIsApproved && "border-dashed border-2"
                )}
                style={{
                  backgroundColor: requestIsApproved
                    ? toCssColorValue(categoryColor, getUIKitColorHSLA("brand-dark", 0.2))
                    : "",
                  borderColor: !requestIsApproved
                    ? toCssColorValue(categoryColor, getUIKitColorHSLA("brand-dark", 0.2))
                    : "",
                }}
              />
            )}
          </Stack>
        </TooltipTrigger>
        <TooltipContent>{renderTooltipContent(holidays, requests)}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <Stack gap={"xs"}>
      <Title
        size="md"
        header={renderDate(new Date(year, monthIndex, 1), "LLLL")}
        className="capitalize text-dark/90"
      />
      <FullCalendar
        key={`${year}-${monthIndex}`}
        plugins={[dayGridPlugin]}
        initialView="dayGridMonth"
        initialDate={new Date(year, monthIndex, 1)}
        headerToolbar={false}
        locale={locale}
        dayHeaderFormat={{ weekday: "narrow" }}
        dayCellClassNames={dayCellClassNames}
        dayCellContent={dayCellContent}
        height="auto"
        fixedWeekCount={false}
      />
    </Stack>
  );
});

export function EmployeeTimeOffCalendar({
  data,
  isLoading,
  year,
  renderDayContent,
}: EmployeeTimeOffCalendarProps) {
  const { data: employee } = useEmployeeMe();

  const fullCalendarLocale = useMemo(() => {
    if (!employee) return enLocale;
    if (employee.language === "ru") return ruLocale;
    return enLocale;
  }, [employee]);

  const dayInfoMap = useMemo(() => buildEmployeeTimeOffDayInfoMap([data]), [data]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-8">
        {[...Array(12)].map((_, index) => (
          <div key={`skeleton-${index}`} className="p-4">
            <Skeleton className="h-6 w-1/3 mb-4" />
            <Skeleton className="h-48 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-10 employee-time-off-calendar">
      {[...Array(12)].map((_, index) => (
        <CalendarMonth
          key={`${year}-${index}`}
          monthIndex={index}
          year={year}
          dayInfoMap={dayInfoMap}
          locale={fullCalendarLocale}
          renderDayContent={renderDayContent}
        />
      ))}
    </div>
  );
}
